// Effective per-platform on/off flag for a row in a rollout (Pre-P2b-3 §2 + §3).
//
// Two layers of control:
//   1. Account.runsMeta / Account.runsGoogle — boolean per Account. Default
//      true on freshly-created Accounts; legacy Accounts may omit the field
//      entirely, in which case we treat them as true (no behaviour change).
//   2. AccountCampaign.metaEnabled / googleEnabled (Pre-P2b-3 §3) — boolean
//      per row. Three-state semantic: true = forced on, false = forced off,
//      null/undefined = inherit from the Account.
//
// The effective flag drives:
//   - Master grid dimming (§2.2): when off, Meta or Google column groups
//     render dimmed and non-interactive.
//   - Row editor section sidebar suffix "(disabled — Account doesn't run
//     Meta)" + body dimmed.
//   - Future Meta / Google grid filtering (P2b-3): rows whose effective flag
//     is false don't appear at all.
//   - Daily-budget cell rendering: cell shows the disabled state when the
//     row's platform is effectively off (computeDailyBudget stays pure
//     numeric — the gating happens at the cell level).
//
// Pure: takes only the two flags and returns a boolean. Reused by
// MasterGrid, RowEditor, and the future grids without coupling them to
// the Account type beyond the single field they care about.
//
// In commit 2 (Pre-P2b-3 §2) the rowOverride arg goes unused — every call
// site passes undefined since the row-level field doesn't exist yet. The
// signature is stable so commit 3 (§3) flips on the row override without
// touching the helper or its consumers.

// Phase 2a — the WRITE-side companion to the effective* read helpers below.
//
// Services became the single source of truth for whether an Account runs on
// Meta / Google: `runsMeta` / `runsGoogle` are now DERIVED-ON-SAVE from the
// services array (the standalone toggles are gone from the UI). The stored
// flags stay — the 113 read-sites all go through effectiveRunsMeta /
// effectiveRunsGoogle, which keep reading `acc.runsMeta` / `acc.runsGoogle` —
// so only the write path changed: on save, `runsMeta = 'meta_ads' ∈ services`,
// `runsGoogle = 'google_ads' ∈ services`. Pure, so it's the same derivation on
// the server (createAccount / updateAccount) and the client (toInput mirror).
export function deriveRunFlagsFromServices(
  services: readonly string[],
): { runsMeta: boolean; runsGoogle: boolean } {
  return {
    runsMeta: services.includes('meta_ads'),
    runsGoogle: services.includes('google_ads'),
  };
}

// Campaign channels (2026-10-24). A campaign can run on Both channels, Meta
// only or Google only. ABSENT means 'both': every campaign created before the
// field existed carries none, and reading absence as 'both' is what keeps
// them unchanged with no migration.
export type CampaignChannels = 'both' | 'meta' | 'google';
export type AdChannel = 'meta' | 'google';

export function campaignIncludesChannel(
  campaignChannels: CampaignChannels | null | undefined,
  channel: AdChannel,
): boolean {
  if (campaignChannels === 'meta') return channel === 'meta';
  if (campaignChannels === 'google') return channel === 'google';
  return true;
}

// ⚠️⚠️ THE ONE CHANNEL GATE. A channel is active for a location only if ALL of:
//   1. the CAMPAIGN includes it;
//   2. the row override (three-state) does not force it off, and
//   3. the account runs it (its service), unless the row forces it ON.
// ⚠️ A campaign that excludes a channel ALWAYS wins, even over a row override
// of `true`: the override means "this location chose it", and a location
// cannot choose an ad the campaign does not have. The pre-existing rule
// between the other two layers is unchanged (row true beats an account that
// does not run the channel).
// ⚠️ Do not model "campaign excludes Google" by seeding row overrides to
// false: those overrides mean "the location chose this" and must stay
// distinguishable from a campaign-level decision.
export function isChannelActive(
  channel: AdChannel,
  inputs: {
    campaignChannels?: CampaignChannels | null;
    accountRuns: boolean | undefined;
    rowEnabled?: boolean | null;
  },
): boolean {
  if (!campaignIncludesChannel(inputs.campaignChannels, channel)) return false;
  if (inputs.rowEnabled === true) return true;
  if (inputs.rowEnabled === false) return false;
  return inputs.accountRuns ?? true;
}

export function effectiveRunsMeta(
  accountFlag: boolean | undefined,
  rowOverride?: boolean | null,
  campaignChannels?: CampaignChannels | null,
): boolean {
  return isChannelActive('meta', {
    campaignChannels,
    accountRuns: accountFlag,
    rowEnabled: rowOverride,
  });
}

export function effectiveRunsGoogle(
  accountFlag: boolean | undefined,
  rowOverride?: boolean | null,
  campaignChannels?: CampaignChannels | null,
): boolean {
  return isChannelActive('google', {
    campaignChannels,
    accountRuns: accountFlag,
    rowEnabled: rowOverride,
  });
}

// Both channels for one row, in one call: what the preview, the preview-send
// eligibility and the preview-card wording all read.
export function activeChannelsForRow(inputs: {
  campaignChannels?: CampaignChannels | null;
  accountRunsMeta: boolean | undefined;
  accountRunsGoogle: boolean | undefined;
  rowMetaEnabled?: boolean | null;
  rowGoogleEnabled?: boolean | null;
}): { meta: boolean; google: boolean } {
  return {
    meta: effectiveRunsMeta(inputs.accountRunsMeta, inputs.rowMetaEnabled, inputs.campaignChannels),
    google: effectiveRunsGoogle(
      inputs.accountRunsGoogle,
      inputs.rowGoogleEnabled,
      inputs.campaignChannels,
    ),
  };
}

export const NO_CHANNELS_REASON = 'No ad channels are on for this location';
